// Day-14 CTA banner — shown on dashboard when a Free user is eligible
// for the Confidence Preview (or has an unredeemed offer already).
//
// Three visual states:
//   1. Eligible, no offer yet → "Ready for a 90-second confidence check?"
//   2. Offer active, not redeemed → "Your offer is still waiting (N days left)"
//   3. Offer expired → hidden (user falls back to Day-21 soft fallback flow)

import { Sparkles } from 'lucide-react';
import type { Day14Offer, UserProfile } from '@shared/types';
import { RECALIBRATION_FLAGS } from '@shared/constants';
import { useFeatureFlagStore } from '@shared/state';
import { THICK_TILE_SHADOW } from '../dashboard/tileStyles';

interface Props {
  user: UserProfile;
  offer: Day14Offer | null;
  onStart: () => void;
  /** Fires on Learner+ users — opens the Confidence Check wizard instead of the Preview. */
  onStartCheck?: () => void;
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function Day14CtaBanner({ user, offer, onStart, onStartCheck }: Props) {
  const previewEnabled = useFeatureFlagStore((s) => s.isEnabled(RECALIBRATION_FLAGS.DAY14_PREVIEW));
  const checkEnabled = useFeatureFlagStore((s) => s.isEnabled(RECALIBRATION_FLAGS.DAY14_CHECK));
  const eligibleAt = (user as unknown as { day14EligibleAt?: string }).day14EligibleAt;
  const variant = (user as unknown as { day14Variant?: 'preview' | 'check' }).day14Variant;
  const isPaid = user.subscription !== 'free';

  // Paid-tier branch: Confidence Check. Hide once eligible flag is cleared
  // (set to null by the server after completion — no offer doc involved).
  if (isPaid) {
    if (!checkEnabled || !eligibleAt || variant !== 'check') return null;

    const onClick = onStartCheck ?? onStart;
    return (
      <div
        onClick={onClick}
        className="relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
        style={{
          background:
            'linear-gradient(135deg, rgba(108,92,231,0.12), rgba(163,155,224,0.06))',
          border: '1px solid rgba(108,92,231,0.30)',
          padding: '18px 22px',
          boxShadow: THICK_TILE_SHADOW,
        }}
      >
        <div
          className="absolute pointer-events-none"
          style={{
            top: -40,
            right: -20,
            width: 160,
            height: 160,
            background:
              'radial-gradient(circle, rgba(108,92,231,0.10) 0%, transparent 70%)',
          }}
        />
        <div className="flex items-center gap-4 relative">
          <div
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, rgba(108,92,231,0.30), rgba(163,155,224,0.20))',
              border: '1px solid rgba(108,92,231,0.40)',
            }}
          >
            <Sparkles size={18} className="text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-indigo-300 font-semibold mb-0.5">
              Confidence Check · 3–5 min
            </div>
            <h3 className="font-heading font-semibold text-[15px] text-white leading-tight mb-0.5">
              Sharpen your profile — Day 14
            </h3>
            <p className="text-[12px] text-content-muted m-0 leading-snug">
              A quick retest to strengthen confidence where your profile is still finding its shape.
            </p>
          </div>
          <div
            className="shrink-0 rounded-lg text-xs font-semibold text-white"
            style={{
              padding: '8px 14px',
              background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
            }}
          >
            Start check
          </div>
        </div>
      </div>
    );
  }

  // Free-tier branch: Confidence Preview + Day-14 Offer.
  // Hide if user isn't yet eligible and no offer exists.
  if (!previewEnabled) return null;

  // Day-21 soft fallback (§2.7): the Day-14 offer expired unredeemed — reassure
  // the user a fresh Confidence Preview is coming (the day-104 re-trigger).
  const day21FallbackAt = (user as unknown as { day21FallbackAt?: string }).day21FallbackAt;
  const offerExpired = offer ? daysUntil(offer.offerExpiresAt) <= 0 : false;
  if (day21FallbackAt && offerExpired && !offer?.redeemedAt) {
    const nextIso = (offer as unknown as { nextOfferEligibleAt?: string | null }).nextOfferEligibleAt ?? null;
    const nextDays = nextIso ? daysUntil(nextIso) : null;
    return (
      <div
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(108,92,231,0.08), rgba(163,155,224,0.04))',
          border: '1px solid rgba(108,92,231,0.22)',
          padding: '16px 20px',
          boxShadow: THICK_TILE_SHADOW,
        }}
      >
        <div className="flex items-center gap-4">
          <div
            className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(108,92,231,0.18)', border: '1px solid rgba(108,92,231,0.30)' }}
          >
            <Sparkles size={16} className="text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-[14px] text-white leading-tight mb-0.5">
              Still curious?
            </h3>
            <p className="text-[12px] text-content-muted m-0 leading-snug">
              {nextDays && nextDays > 0
                ? `Free members get a fresh Confidence Preview in ${nextDays} day${nextDays === 1 ? '' : 's'}.`
                : 'Free members get a fresh Confidence Preview every 90 days.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!eligibleAt && !offer) return null;
  if (offer?.redeemedAt) return null;
  const daysLeft = offer ? daysUntil(offer.offerExpiresAt) : null;
  if (offer && daysLeft !== null && daysLeft <= 0) return null;

  const hasUnredeemedOffer = offer && !offer.redeemedAt;
  const headline = hasUnredeemedOffer
    ? 'Your offer is still waiting'
    : 'Ready for a 90-second confidence check?';
  const sub = hasUnredeemedOffer
    ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left on your discounted Learner offer.`
    : 'A quick 3-question check to show you where your profile is sharpest — and where it could be sharper.';
  const cta = hasUnredeemedOffer ? 'Review offer' : 'Start preview';

  return (
    <div
      onClick={onStart}
      className="relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
      style={{
        background:
          'linear-gradient(135deg, rgba(108,92,231,0.12), rgba(240,165,0,0.06))',
        border: '1px solid rgba(108,92,231,0.30)',
        padding: '18px 22px',
        boxShadow: THICK_TILE_SHADOW,
      }}
    >
      <div
        className="absolute pointer-events-none"
        style={{
          top: -40,
          right: -20,
          width: 160,
          height: 160,
          background:
            'radial-gradient(circle, rgba(240,165,0,0.10) 0%, transparent 70%)',
        }}
      />

      <div className="flex items-center gap-4 relative">
        <div
          className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background:
              'linear-gradient(135deg, rgba(108,92,231,0.30), rgba(240,165,0,0.20))',
            border: '1px solid rgba(108,92,231,0.40)',
          }}
        >
          <Sparkles size={18} className="text-gold-500" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-0.5">
            Confidence Preview · Free
          </div>
          <h3 className="font-heading font-semibold text-[15px] text-white leading-tight mb-0.5">
            {headline}
          </h3>
          <p className="text-[12px] text-content-muted m-0 leading-snug">{sub}</p>
        </div>

        <div
          className="shrink-0 rounded-lg text-xs font-semibold text-white"
          style={{
            padding: '8px 14px',
            background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
          }}
        >
          {cta}
        </div>
      </div>
    </div>
  );
}
